import { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react'

const RECAPTCHA_SITE_KEY = import.meta.env.VITE_GOOGLE_RECAPTCHA_SITE_KEY!
const SCRIPT_ID = 'google-recaptcha-v3'

interface ReCaptchaContextType {
  executeRecaptcha: ((action: string) => Promise<string>) | undefined
}

const ReCaptchaContext = createContext<ReCaptchaContextType>({
  executeRecaptcha: undefined,
})

// Module-level flag prevents double-loading across StrictMode remounts
let scriptLoading = false

export function GoogleReCaptchaClientProvider({ children }: { children: React.ReactNode }) {
  const [grecaptcha, setGrecaptcha] = useState<typeof window.grecaptcha | null>(null)
  const widgetRef = useRef<number | string | undefined>(undefined)

  useEffect(() => {
    if (scriptLoading) {
      // Script already loading from a previous mount — just poll for readiness
      const interval = setInterval(() => {
        if (window.grecaptcha?.execute) {
          setGrecaptcha(window.grecaptcha)
          clearInterval(interval)
        }
      }, 100)
      return () => clearInterval(interval)
    }

    if (document.getElementById(SCRIPT_ID)) {
      // Script already present (e.g. from a previous session's HMR)
      if (window.grecaptcha?.execute) {
        setGrecaptcha(window.grecaptcha)
      }
      return
    }

    scriptLoading = true
    const script = document.createElement('script')
    script.id = SCRIPT_ID
    script.src = `https://www.google.com/recaptcha/api.js?render=${RECAPTCHA_SITE_KEY}`
    script.async = true
    script.defer = true
    script.onload = () => {
      window.grecaptcha!.ready(() => {
        setGrecaptcha(window.grecaptcha!)
      })
    }
    document.head.appendChild(script)

    // Intentionally no cleanup — removing the global reCAPTCHA script
    // corrupts state. See https://github.com/google/recaptcha/issues/269
  }, [])

  const executeRecaptcha = useCallback(
    (action: string): Promise<string> => {
      if (!grecaptcha?.execute) {
        return Promise.reject(new Error('reCAPTCHA not ready'))
      }
      return grecaptcha.execute(widgetRef.current ?? RECAPTCHA_SITE_KEY, { action })
    },
    [grecaptcha],
  )

  return (
    <ReCaptchaContext value={{ executeRecaptcha: grecaptcha ? executeRecaptcha : undefined }}>
      {children}
    </ReCaptchaContext>
  )
}

export function useGoogleReCaptcha() {
  return useContext(ReCaptchaContext)
}
